import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MenuController } from '@ionic/angular/standalone';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  CompanyService,
  ProinfraBasisLoadingSpinnerService,
} from '@profi/proinfra-shared-angular';
import { StorageService } from '@services/storage.service';
import {
  AuthenticationRequest,
  AuthService,
  Company,
  User,
  UserRole,
} from '@openapi';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private userSubject: BehaviorSubject<User>;
  public user$: Observable<User>;

  private userRolesSubject: BehaviorSubject<UserRole[]>;
  public userRoles$: Observable<UserRole[]>;

  constructor(
    private router: Router,
    private authService: AuthService,
    private menu: MenuController,
    private storageService: StorageService,
    private loadingSpinnerService: ProinfraBasisLoadingSpinnerService,
    private translateService: TranslateService,
    private companyService: CompanyService
  ) {
    const storedUser = this.getStoredUser();
    this.userSubject = new BehaviorSubject<User>(storedUser);
    this.user$ = this.userSubject.asObservable();

    this.userRolesSubject = new BehaviorSubject<UserRole[]>([]);
    this.userRoles$ = this.userRolesSubject.asObservable();
  }

  /** Returns the current user value. */
  public get userValue(): User {
    return this.userSubject.value;
  }

  /**
   * Authenticates the user and initializes their session.
   *
   * @param username - The user's login identifier.
   * @param password - The user's password.
   * @param companyId - The selected company ID.
   * @returns An observable of the authenticated user.
   */
  public login(
    username: string,
    password: string,
    companyId: number
  ): Observable<User> {
    const authenticationRequest: AuthenticationRequest = {
      useridentifier: username,
      password,
      companyId,
    };

    return this.authService.authenticate(authenticationRequest).pipe(
      tap((response: User & { token?: string }) => {
        if (response) {
          this.setUserSession(response);
          if (response.token) {
            localStorage.setItem('authToken', response.token);
          } else {
            console.warn('No token in login response');
          }
        } else {
          console.warn('No response from login');
          this.router.navigate(['/login']);
        }
      }),
      catchError(error => {
        console.error('Login error:', error);
        this.loadingSpinnerService.show(
          this.translateService.instant('labelsAuthContext.loginFailed')
        );
        return of(null);
      })
    );
  }

  /**
   * Logs out the user and clears all session data.
   */
  public logout(): void {
    this.clearSessionData();
    this.authService.logout().subscribe({
      next: () => this.router.navigate(['/login']),
      error: error => console.error('Logout error:', error),
    });
    location.reload();
  }

  /**
   * Retrieves the user's roles from the backend and updates the state.
   *
   * @returns An observable of user roles.
   */
  public getUserRoles(): Observable<UserRole[]> {
    const token = localStorage.getItem('authToken');

    return this.authService.getAuthData().pipe(
      tap((userRoles: UserRole[]) => {
        if (userRoles) {
          this.userRolesSubject.next(userRoles);
        } else {
          this.logoutInvalidSession();
        }
      }),
      catchError(error => {
        console.error('Get user roles error:', error);
        this.loadingSpinnerService.show(
          this.translateService.instant('labelsAuthContext.sessionExpired')
        );
        this.logout();
        return of([]);
      })
    );
  }

  /**
   * Forces logout due to an invalid session (e.g. expired token).
   */
  public logoutInvalidSession(): void {
    this.loadingSpinnerService.show(
      this.translateService.instant('labelsAuthContext.sessionExpired')
    );
    this.logout();
  }

  /**
   * Combines user and roles into a single observable structure.
   *
   * @returns Observable containing both the user and their roles.
   */
  public getUserDataWithRolesObservable(): Observable<{
    user: User;
    roles: UserRole[];
  }> {
    return combineLatest([this.user$, this.userRoles$]).pipe(
      map(([user, roles]) => ({ user, roles }))
    );
  }

  /**
   * Retrieves the user object from localStorage.
   *
   * @returns Parsed `User` object or `null`.
   */
  private getStoredUser(): User {
    return JSON.parse(localStorage.getItem('user') || 'null');
  }

  /**
   * Sets session data in memory and localStorage after successful login.
   *
   * @param user - The authenticated user.
   */
  setUserSession(user: User): void {
    this.menu.enable(true);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('firstName', user.firstName);
    localStorage.setItem('lastName', user.lastName);
    localStorage.setItem('roleName', user.roleName);
    localStorage.setItem('isLoggedIn', JSON.stringify(true));
    this.userSubject.next(user);
  }

  /**
   * Retrieves the company ID from the stored user object.
   *
   * @returns Company ID as a string (empty if unavailable).
   */
  public retriveCompanyId(): string {
    let companyId = '';
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const user = JSON.parse(userString);
        companyId = user.companyId;
      } catch (error) {
        console.error('Error parsing user object:', error);
      }
    }
    return companyId;
  }

  /**
   * Clears all session-related data from storage and memory.
   */
  private clearSessionData(): void {
    const companyId = this.retriveCompanyId();

    localStorage.clear();
    this.storageService.clear();

    // Clear all IndexedDB databases
    indexedDB.databases().then(databases => {
      databases.forEach(db => {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
        }
      });
    });

    localStorage.setItem('companyId', companyId); // Retain companyId post-clear
    this.userSubject.next(null);
    this.userRolesSubject.next([]);
  }

  /**
   * Retrieves all companies from the backend.
   *
   * @returns An observable of the list of companies.
   */
  public getAllCompanies(): Observable<Company[]> {
    return this.companyService.getCompanies();
  }

  /**
   * Retrieves a single company by its ID.
   *
   * @param id - The company ID.
   * @returns An observable of the company data.
   */
  public getCompanyById(id: number): Observable<Company> {
    return this.companyService.getCompanyById(id);
  }
}
